//services/[parent]/[child].js

import Head from "next/head";
import { useRouter } from "next/router";
import LoadingSpinner from "../../../components/common/LoadingSpinner";
import SubServicesDetails from "../../../components/sub-services-details";
import SubServicesProcess from "../../../components/sub-services-process";
import GrandSubServicesList from "../../../components/grand-sub-services-list";
import FaqSection from "../../../components/common/FaqSection";
import Footer from "../../../components/footer";
import Header from "../../../components/common/Header";
import ServicesBanner from "../../../components/common/ServicesBanner";
import {
  fetchSubServicesDetails,
  // fetchSubServicesData,
} from "../../../components/http/commonApi";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import { addDofollowToAnchors } from "../../../utils/modifyHtmlLinks";
import HreflangLinks from "../../../components/common/HrefLinkTags";

function prettifySlug(slug = "") {
  return String(slug)
    .split("-")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

/**
 * Some API fields (bulletPoints, subservices, etc.) have been observed
 * coming back as either a plain object (`{ title, points: [...] }`) or
 * an array wrapping that same object (`[{ title, points: [...] }]`).
 * Normalize both shapes to the plain object form so the rest of the
 * component doesn't have to guess or force `[0]` on something that
 * might not be an array.
 */
function firstOrSelf(value) {
  if (!value) return null;
  if (Array.isArray(value)) return value[0] ?? null;
  return value;
}

/**
 * Strips HTML tags/entities and collapses whitespace so we can
 * compare the *text content* of two HTML strings regardless of minor
 * markup/formatting differences.
 */
function stripHtml(html = "") {
  return String(html)
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();
}

/**
 * Strips HTML tags/entities and removes ALL whitespace (not just
 * collapsing it), for a strict text-only comparison. Some API points
 * have `pointTitle` as the heading + description glued together with
 * no space (e.g. "TitleDescription starts here...") while
 * `pointDescription` has the same text properly wrapped in HTML
 * (`<h3>Title</h3><p>Description starts here...</p>`), which turns
 * into "Title Description..." once tags become spaces. Stripping all
 * whitespace makes both forms comparable.
 */
function normalizeForCompare(str = "") {
  return stripHtml(str).replace(/\s+/g, "");
}

/**
 * Given a `newprocess.points` array, returns a copy where any point
 * whose `pointTitle` is just the same text as its `pointDescription`
 * (title + description glued together, or duplicated) has its
 * `pointTitle` cleared out. `pointDescription` already contains the
 * proper heading (`<h3>`) + paragraph markup, so once `pointTitle` is
 * cleared the UI only renders the description — no more duplicate
 * title/description showing side by side.
 */
function dedupeProcessPoints(points) {
  if (!Array.isArray(points)) return points;

  return points.map((point) => {
    const titleText = normalizeForCompare(point?.pointTitle);
    const descriptionText = normalizeForCompare(point?.pointDescription);

    const isDuplicate =
      titleText &&
      descriptionText &&
      (titleText === descriptionText || descriptionText.includes(titleText));

    if (!isDuplicate) return point;

    return {
      ...point,
      pointTitle: "",
    };
  });
}

export default function SubServicesInner({
  serviceDetails,
  parentslug,
  locale,
  slug,
}) {
  const router = useRouter();

  if (router.isFallback) {
    return <LoadingSpinner message="Loading service details..." />;
  }

  const newprocessBlock = serviceDetails?.newprocess;
  const subservicesBlock = firstOrSelf(serviceDetails?.subservices);

  const processPoints = dedupeProcessPoints(newprocessBlock?.points);
  const showProcess = processPoints?.length > 0;

  return (
    <>
      {/* <HreflangLinks
        enPath={`/${parentslug}/${serviceDetails?.slug_en}`}
        arPath={`/${parentslug}/${serviceDetails?.slug_en}`} // same slug, just under /ar
      /> */}

      <HreflangLinks
        enPath={`/${parentslug}/${serviceDetails?.slug_en}`}
        arPath={
          serviceDetails?.hasArabic
            ? `/ar/${parentslug}/${serviceDetails.slug_en}`
            : null
        }
      />

      <Head>
        <title>
          {serviceDetails?.metaTitle ||
            "Prism Digital | Top Digital Marketing Agency in UAE"}
        </title>
        <meta
          name="description"
          content={
            serviceDetails?.metaDescription ||
            "A leading digital marketing agency in UAE - Prism Digital, offering SEO, PPC, social media, and web design services to grow your brand online. Book us today!"
          }
        />
        {locale === "ar" ? (
          <link
            rel="canonical"
            href={`https://www.prism-me.com/${locale}/${parentslug}/${serviceDetails?.slug_en}`}
          />
        ) : (
          <link
            rel="canonical"
            href={`https://www.prism-me.com/${parentslug}/${serviceDetails?.slug_en}`}
          />
        )}
      </Head>

      <Header />
      <main className="w-full box-border bg-background-color-primary overflow-hidden">
        {/* <CommonBanner2 name={serviceDetails?.bannertitle} /> */}
        <ServicesBanner
          name={serviceDetails?.bannertitle} // ✅ child banner title changes by lang
          breadcrumbProps={{
            items: [
              {
                label: locale === "ar" ? "الرئيسية" : "Home",
                href: locale === "ar" ? "/ar" : "/",
              },

              // ✅ Parent: label from API (lang-based), href from EN slug
              {
                label:
                  serviceDetails?.parent?.menuname || prettifySlug(parentslug),
                href:
                  locale === "ar"
                    ? `/ar/${serviceDetails?.parent?.slug_en || parentslug}`
                    : `/${serviceDetails?.parent?.slug_en || parentslug}`,
              },

              // ✅ current: child title (lang-based)
              { label: serviceDetails?.menuname, current: true },
            ],
            separator: locale === "ar" ? "‹" : ">",
          }}
          slug={slug}
        />

        <SubServicesDetails
          serviceIntro={{
            title: serviceDetails?.title,
            description: serviceDetails?.description,
          }}
        />

        {/*
          Duplicate title/description within newprocess points is
          handled by dedupeProcessPoints() above: it clears pointTitle
          when it's just the description glued together, so
          SubServicesProcess renders the description (with its own
          <h3> heading) once instead of showing it twice.
        */}
        {showProcess && (
          <SubServicesProcess
            title={newprocessBlock?.title}
            processData={processPoints}
          />
        )}

        <div className="bg-text-primary text-background-color-primary w-full py-[40px] lg:py-[60px]">
          <section
            className={`w-full flex flex-col gap-6 items-start justify-center px-6 sm:px-6 lg:px-20 box-border max-w-[1600px] mx-auto`}
          >
            <h2 className="m-0 text-[28px] sm:text-[32px] lg:text-[40px] leading-[120%] font-medium">
              {serviceDetails?.secondaryHeading}
            </h2>
            <div
              className="text-[16px] leading-[150%] m-0 rich-text"
              dangerouslySetInnerHTML={{
                __html:
                  addDofollowToAnchors(serviceDetails?.secondaryDescription) ||
                  "",
              }}
            />
            {/* {serviceDetails?.secondaryDescription}
            </p> */}
          </section>
        </div>
        {subservicesBlock && (
          <GrandSubServicesList
            title={subservicesBlock?.title}
            subServices={subservicesBlock?.content}
            grandchildServices={serviceDetails?.grandchildServices}
            parentslug={parentslug}
            childslug={serviceDetails?.slug_en}
          />
        )}
        <section className="w-full pt-[40px] lg:pt-[60px]" />
        <FaqSection faqs={serviceDetails?.faqs} />
      </main>
      <Footer />
    </>
  );
}

export async function getServerSideProps({ params, locale, res }) {
  try {
    const { data: serviceDetails } = await fetchSubServicesDetails(
      params.child,
      locale,
    );

    if (!serviceDetails) {
      return {
        notFound: true,
      };
    }

    // ✅ Set cache headers
    res.setHeader(
      "Cache-Control",
      "public, s-maxage=60, stale-while-revalidate=300",
    );

    return {
      props: {
        serviceDetails,
        parentslug: params.parent,
        slug: params.child,
        locale,
        ...(await serverSideTranslations(locale, [
          "header",
          "footer",
          "services",
          "common",
        ])),
      },
    };
  } catch (error) {
    console.error("Error in getServerSideProps (services):", error);
    return {
      notFound: true,
    };
  }
}
